import { useCallback } from 'react';

import { htmlToSlate, slateToText } from 'src/components/RichSlate';
import { mapAnnotationForGeneration } from 'src/data/annotation';
import { mapToolContentForBriefInput } from 'src/data/brief';
import { getCustomModelTexts } from 'src/data/customModel';
import { AI_SUGGESTION_MODE, getAlignedSuggestionMode } from 'src/data/generation';
import {
  doesToolSupportCallToAction,
  doesToolSupportCustomModel,
  doesToolSupportImageGeneration,
  doesToolSupportManualInstructions,
  doesToolSupportPromptInstruction,
  GENERATION_TOOL,
  getAvailableGenerationTools,
  isFreestyleGenerationTool,
  isMixtureTool,
  shouldDisableTOVModes,
} from 'src/data/generationTool';
import { PROJECT_TYPE } from 'src/data/project';
import { mapScoringMethodForMutation } from 'src/data/scoring';
import { TEMPLATE_BUILDER_PARAMS } from 'src/data/template';
import { getToneOfVoiceForGeneration, getToneOfVoiceForInfinity } from 'src/data/toneOfVoice';
import { useBrandVoicesAssets, useProjectActions } from 'src/store';
import { useMultiPromptTemplateActions } from 'src/store/multiPromptTemplates';

import { useDocumentViewActions } from '../../../store';
import { useLocalActions } from '../store';

export const useCollectSuggestParams = () => {
  const documentViewActions = useDocumentViewActions();
  const projectActions = useProjectActions();
  const localActions = useLocalActions();
  const { toneOfVoices, customModels } = useBrandVoicesAssets();
  const multiPromptTemplateActions = useMultiPromptTemplateActions();

  return useCallback(() => {
    const generationTool = localActions.getGenerationTool();
    const subGenerationToolsProps = isMixtureTool(generationTool)
      ? localActions.getFilteredSubGenerationToolsProps()
      : null;
    // Brief
    const briefContent = localActions.getBriefContent();
    const brief = mapToolContentForBriefInput(generationTool, briefContent);

    // Multi prompt
    const promptFields = localActions.getCsvId()
      ? {}
      : localActions.getMultiPromptFieldsForGeneration();
    const promptFieldImages = localActions.getMultiPromptFieldImages();
    const promptFieldDocuments = localActions.getMultiPromptFieldDocuments();
    const customMultiPromptId =
      generationTool === GENERATION_TOOL.customMultiPrompt
        ? localActions.getCustomMultiPromptId()
        : null;

    const customMultiPromptTemplate = customMultiPromptId
      ? multiPromptTemplateActions.getMultiPromptTemplate(customMultiPromptId)
      : null;

    // Modes
    const modeId = localActions.getGenerationModeId();
    const modeType = localActions.getGenerationModeType();
    const suggestionMode = getAlignedSuggestionMode(modeId, modeType);
    const customFormulaId = suggestionMode === AI_SUGGESTION_MODE.customFormula ? modeId : null;
    const isQuotesMode = modeId === AI_SUGGESTION_MODE.quoteUrl;
    const toolList = getAvailableGenerationTools({ projectType: PROJECT_TYPE.document });

    // Additional params
    const persona = localActions.getPersonaForGeneration();
    const scoringMethod = documentViewActions.getScoringMethod();

    const getAnnotations = () => {
      if (isQuotesMode) {
        return [];
      }

      const annotations = localActions.getAnnotationsForGeneration();
      return annotations.map(mapAnnotationForGeneration);
    };

    const promptImageIds = localActions.getPromptImageIds();
    const promptDocumentIds = localActions.getPromptDocumentIds();

    const params = {
      generationTool,
      subGenerationToolsProps,
      briefContent,
      brief,
      suggestionMode,
      modeId,
      modeType,
      customFormulaId,
      annotations: getAnnotations(),
      customerPersona: persona,
      numberOfVariations: localActions.getNumberOfVariations(),
      language: localActions.getLanguageForGeneration(),
      languageForLoader: localActions.getLanguage(),
      languageTargetFormality: localActions.getLanguageTargetFormality(),
      scoringMethod: mapScoringMethodForMutation(scoringMethod),
      promptFields,
      customMultiPromptId,
      promptImageIds,
      promptDocumentIds,
      promptFieldImages,
      promptFieldDocuments,
      csvId:
        customMultiPromptTemplate?.templateType === TEMPLATE_BUILDER_PARAMS.csv
          ? localActions.getCsvId()
          : null,
      availableTools: JSON.stringify(toolList),
      structureInstructionsData: localActions.getStructureInstructionsData(),
    };

    if (!isQuotesMode && !shouldDisableTOVModes(generationTool)) {
      const selectedTov = projectActions.getSelectedTov();
      const draftTov = projectActions.getDraftToneOfVoice();
      params.tovInput = getToneOfVoiceForGeneration(selectedTov, draftTov);
      params.tov = getToneOfVoiceForInfinity(selectedTov, toneOfVoices);
    }

    if (doesToolSupportManualInstructions(generationTool)) {
      params.manualInstructions = localActions.getManualInstructions();
    }

    if (doesToolSupportCallToAction(generationTool)) {
      params.callToAction = localActions.getCallToAction();
    }

    const generationCustomModelId = projectActions.getSelectedCustomModelId();
    if (doesToolSupportCustomModel(generationTool) && generationCustomModelId) {
      params.generationCustomModelId = generationCustomModelId;
      params.customFormulaId = null;
      params.tovInput = null;
      const { customModelTitle } = getCustomModelTexts(generationCustomModelId, customModels);
      params.tov = customModelTitle;
    }

    if (doesToolSupportImageGeneration(generationTool)) {
      params.imageAspectRatio = localActions.getImageAspectRatio();
      params.imageModel = localActions.getImageModel();
    }

    if (
      isFreestyleGenerationTool(generationTool) ||
      doesToolSupportPromptInstruction(generationTool) ||
      (localActions.inExtensionPromptMode() && generationTool === GENERATION_TOOL.googleRSA)
    ) {
      // Specific params for "freestyle" tool
      const freestyleToolContent = localActions.getFreestyleToolContent();
      const instructions = freestyleToolContent.allInstructions;
      const instructionsData = freestyleToolContent.allInstructionsData;
      params.freestyleToolId = freestyleToolContent?.id;
      params.freestyleToolTitle = freestyleToolContent?.name;
      params.freestyleToolAllInstructions = instructions;
      params.freestyleToolAllInstructionsData = instructionsData;

      if (params.freestyleToolAllInstructionsData) {
        params.briefContent = null;
        params.brief = {};

        // Provide freshly serialized instructions HTML to Infinity, cause "serialized" value in existing "saved prompts" might lack newlines
        if (localActions.inExtensionPromptMode()) {
          const slateValue = htmlToSlate(params.freestyleToolAllInstructionsData.html);
          params.freestyleToolAllInstructionsData = {
            ...params.freestyleToolAllInstructionsData,
            text: slateToText(slateValue),
          };
        }
      }
    }

    // Some parameters which are exclusive to Infinity extension or should be cleared before sending there
    if (localActions.inExtensionPromptMode()) {
      params.tov = null; // TOV is not needed for Infinity
    }

    return params;
  }, [
    documentViewActions,
    localActions,
    projectActions,
    toneOfVoices,
    customModels,
    multiPromptTemplateActions,
  ]);
};
